/**
 * Domain Configuration Utility
 *
 * Dynamically detects the current domain and provides environment-specific URLs.
 * Supports multiple deployments:
 * - kiedynabory.pl (production)
 * - staging.kiedynabory.pl (KiedyNabory staging)
 * - hotelb2b.kukla.tech (staging/dev)
 * - localhost (development)
 *
 * All environment-specific hosts (API, app, landing, tenant subdomains) are
 * DERIVED from a single "environment base" (`resolveEnvBaseForHostname`), so a
 * brand's addresses are never hardcoded per environment. Only brand identity
 * (name, support email, docs) is constant — a brand name is not an address.
 */

/**
 * Resolve the "environment base" host for a frontend hostname — the eTLD+1
 * (production) or its staging sibling that every environment-specific address
 * is derived from.
 *
 *   kiedynabory.pl             → "kiedynabory.pl"          (prod)
 *   app.kiedynabory.pl         → "kiedynabory.pl"          (prod)
 *   moja-firma.kiedynabory.pl  → "kiedynabory.pl"          (prod)
 *   staging.kiedynabory.pl     → "staging.kiedynabory.pl"  (staging)
 *   app.staging.kiedynabory.pl → "staging.kiedynabory.pl"  (staging)
 *   hotelb2b.kukla.tech        → "hotelb2b.kukla.tech"
 *
 * Staging is matched FIRST: `*.staging.kiedynabory.pl` endsWith
 * ".kiedynabory.pl", so without checking it before the production suffix it
 * would resolve to the production base and EVERY derived address (API, app,
 * landing, tenant redirect) would silently point at PRODUCTION — the exact
 * failure of incident 2026-07-02.
 *
 * Returns `null` for unknown hosts (localhost, custom tenant domains) so
 * callers can fall back to runtime env / localhost defaults. The exact-match
 * plus leading-dot suffix check is also the suffix-spoof guard:
 * `kiedynabory.pl-evil.com` does not match and resolves to `null`.
 */
export function resolveEnvBaseForHostname(hostname: string): string | null {
  // Staging must be checked before the production suffix it is nested under.
  if (
    hostname === "staging.kiedynabory.pl" ||
    hostname.endsWith(".staging.kiedynabory.pl")
  ) {
    return "staging.kiedynabory.pl";
  }
  if (hostname === "kiedynabory.pl" || hostname.endsWith(".kiedynabory.pl")) {
    return "kiedynabory.pl";
  }
  if (
    hostname === "hotelb2b.kukla.tech" ||
    hostname.endsWith(".hotelb2b.kukla.tech")
  ) {
    return "hotelb2b.kukla.tech";
  }
  return null;
}

/**
 * Resolve the API URL for the given frontend hostname.
 *
 * Priority:
 *  1. Build-time env (`process.env.REACT_APP_API_URL`) — explicit override
 *     for CI / test stack / local dev. Always wins when set.
 *  2. Environment base (`resolveEnvBaseForHostname`) — for known brands and
 *     their staging siblings the API lives at `api.<envBase>`, on the same
 *     eTLD+1 as the frontend, so the cookie set by
 *     `POST /grants/verify-browser` is first-party from the browser's
 *     perspective. Required by Safari ITP and RFC 6265 §5.3 to keep the
 *     Turnstile cookie usable across requests. Without this, all Safari
 *     visitors fail registration on `/register/check-eligibility` because the
 *     cookie is silently dropped as third-party. Staging is matched first
 *     inside `resolveEnvBaseForHostname` (see incident 2026-07-02).
 *  3. Runtime env (`window._env_.REACT_APP_API_URL` from Docker
 *     inject-env.sh) — fallback for unknown hostnames (custom tenant domains
 *     that have not yet configured a same-site `api.<their-domain>` CNAME).
 *     Cross-site cookies will not work in Safari for these tenants on
 *     /grants/calls; they should migrate to a same-site API hostname.
 *  4. localhost development fallback (`http://localhost:3001`).
 *
 * Why env base beats runtime env:
 * Production deployments hardcode `window._env_.REACT_APP_API_URL =
 * "https://api.hotelb2b.kukla.tech"` for all containers (single Docker image,
 * single env injection). Without this priority, frontends served from
 * `app.kiedynabory.pl` would always strike out to the staging API domain,
 * making every cookie cross-site. Hostname-aware routing fixes that without
 * re-architecting the deployment pipeline.
 *
 * @see Bug repro 2026-05-08 (Agnieszka Lebryk on Safari) — registration
 *      failed because the cookie set by api.hotelb2b.kukla.tech was blocked
 *      by Safari ITP for the app.kiedynabory.pl origin.
 * @see Incident 2026-07-02 — app.staging.kiedynabory.pl served pages that
 *      called the PRODUCTION api.kiedynabory.pl (the staging host was matched
 *      by the production suffix before the staging carve-out), so KiedyNabory
 *      staging was untestable — every request hit prod.
 */
export function resolveApiUrlForHostname(hostname: string): string {
  // 1. Build-time env always wins (CI, test stack, local override)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. Same-site API for known brands / staging siblings: api.<envBase>.
  // Keeps the Turnstile cookie first-party (Safari ITP). Staging is resolved
  // ahead of the production suffix inside resolveEnvBaseForHostname.
  const envBase = resolveEnvBaseForHostname(hostname);
  if (envBase) {
    return `https://api.${envBase}`;
  }

  // 3. Runtime env override — used by custom tenant domains that haven't
  // configured a same-site API yet (cross-site, broken in Safari).
  if (typeof window !== "undefined" && window._env_?.REACT_APP_API_URL) {
    return window._env_.REACT_APP_API_URL;
  }

  // 4. localhost development fallback
  return "http://localhost:3001";
}

export interface DomainConfig {
  baseDomain: string;
  appDomain: string;
  landingDomain: string;
  apiUrl: string;
  supportEmail: string;
  brandName: string;
  brandDescription: string;
  docsUrl?: string;
}

/**
 * Get current domain configuration based on window.location.hostname.
 *
 * Environment-specific addresses (baseDomain, appDomain, landingDomain,
 * apiUrl) are DERIVED from the environment base (`resolveEnvBaseForHostname`),
 * so staging never leaks production hosts: on `app.staging.kiedynabory.pl` the
 * login / landing / tenant-redirect targets all stay under
 * `*.staging.kiedynabory.pl`. Only brand identity (brandName, supportEmail,
 * docsUrl, description) is a constant.
 *
 * @see ../../brand-meta.json — `brands.kiedynabory.hostPatterns` and
 *      `brands.hotelb2b.hostPatterns` must stay synchronized with the hostname
 *      matchers in `resolveEnvBaseForHostname` above. brand-meta.json drives
 *      nginx OG/meta substitution, this function drives runtime UI (brandName,
 *      supportEmail, etc.). Long-term: import JSON directly (separate ADR).
 */
export function getDomainConfig(): DomainConfig {
  const hostname = window.location.hostname;
  const envBase = resolveEnvBaseForHostname(hostname);
  const apiUrl = resolveApiUrlForHostname(hostname);

  // KiedyNabory (prod: kiedynabory.pl, staging: staging.kiedynabory.pl).
  // Addresses derive from envBase; brand identity is constant.
  if (envBase && envBase.endsWith("kiedynabory.pl")) {
    return {
      baseDomain: envBase,
      appDomain: `app.${envBase}`,
      landingDomain: envBase,
      apiUrl,
      supportEmail: "info@kiedynabory.pl",
      brandName: "KiedyNabory.pl",
      brandDescription: "Monitoring dotacji i naborów",
      docsUrl: "https://docs.kiedynabory.pl",
    };
  }

  // Hotel B2B (hotelb2b.kukla.tech). No docsUrl - no docs site for this brand.
  if (envBase === "hotelb2b.kukla.tech") {
    return {
      baseDomain: envBase,
      appDomain: `app.${envBase}`,
      landingDomain: envBase,
      apiUrl,
      supportEmail: "support@hotelb2b.kukla.tech",
      brandName: "Hotel B2B",
      brandDescription: "Oferty, analityka i CRM",
    };
  }

  // Development/Test: localhost (dynamic port from window.location.host)
  // Uses actual host:port instead of hardcoded port, supporting worktree-based
  // test environments with dynamically assigned ports (e.g. "localhost:3150").
  // Aligned with brand-meta.json `default: "hotelb2b"` — localhost defaults to
  // Hotel B2B brand (matches nginx OG/meta substitution fallback).
  const host = window.location.host;
  return {
    baseDomain: "localhost",
    appDomain: host,
    landingDomain: host,
    apiUrl,
    supportEmail: "support@hotelb2b.kukla.tech",
    brandName: "Hotel B2B",
    brandDescription: "Oferty, analityka i CRM",
    // No docsUrl: Hotel B2B has no docs site (matches staging branch above).
  };
}

/**
 * Get full login URL for current environment
 * Returns: https://app.{domain}/login or http://localhost:{port}/login
 * (port is dynamic based on window.location, not hardcoded to 3000)
 */
export function getLoginUrl(): string {
  const config = getDomainConfig();
  const protocol = config.appDomain.includes("localhost") ? "http" : "https";
  return `${protocol}://${config.appDomain}/login`;
}

/**
 * Get full landing page URL for current environment
 * Returns: https://{domain} or http://localhost:{port}
 * (port is dynamic based on window.location, not hardcoded to 3000)
 */
export function getLandingUrl(): string {
  const config = getDomainConfig();
  const protocol = config.landingDomain.includes("localhost")
    ? "http"
    : "https";
  return `${protocol}://${config.landingDomain}`;
}

/**
 * Get API base URL for current environment
 * Returns: https://api.{domain} or http://localhost:3001
 */
export function getApiUrl(): string {
  return getDomainConfig().apiUrl;
}

/**
 * Get support email for current environment
 */
export function getSupportEmail(): string {
  return getDomainConfig().supportEmail;
}

/**
 * Get docs URL for current environment (if available for this brand).
 * SECURITY: docsUrl is hardcoded in domain config — never source from API or user input
 * as it is rendered directly as href in JSX.
 * @param path - Optional path to append (e.g., '/troubleshooting/weryfikacja-przegladarki')
 */
export function getDocsUrl(path?: string): string | undefined {
  const docsUrl = getDomainConfig().docsUrl;
  if (!docsUrl) return undefined;
  return path ? `${docsUrl}${path}` : docsUrl;
}

/**
 * Get tenant subdomain URL for post-login redirect
 * @param subdomain - Tenant subdomain (e.g., "moja-firma")
 * @returns Full URL to tenant subdomain (e.g., "https://moja-firma.kiedynabory.pl")
 */
export function getTenantUrl(subdomain: string): string {
  const config = getDomainConfig();
  const protocol = config.baseDomain.includes("localhost") ? "http" : "https";

  // For localhost, redirect to main app domain
  if (config.baseDomain === "localhost") {
    return `${protocol}://${config.appDomain}`;
  }

  // For production/staging, use subdomain
  return `${protocol}://${subdomain}.${config.baseDomain}`;
}
